import { useEffect, useState } from "react"
import { useAuthenticator } from "@aws-amplify/ui-react"
import { getUserDetails, isUserSubscribed } from "../Utils/functions"

/**
 * Shared access-check hook.
 * Fetches subscription + enterprise status in parallel.
 * Eliminates the duplicate checkSubscription / checkEnterpriseStatus pattern
 * that previously lived in LiteratureReviewTools and Screening.
 */
const useAccessCheck = () => {
    const { user } = useAuthenticator((context) => [context.user])
    const [isSubscribed, setIsSubscribed] = useState(false)
    const [isAdvanced, setIsAdvanced] = useState(false)
    const [currentPlan, setCurrentPlan] = useState(null)
    const [isEnterpriseActive, setIsEnterpriseActive] = useState(false)
    const [hasEnterprise, setHasEnterprise] = useState(false)
    const [loading, setLoading] = useState(true)
    const [errorMessage, setErrorMessage] = useState("")

    useEffect(() => {
        if (!user) {
            setLoading(false)
            return
        }

        let cancelled = false

        const checkAccess = async () => {
            setLoading(true)
            try {
                const [subscriptionData, userDetails] = await Promise.all([
                    isUserSubscribed().catch((err) => {
                        if (!cancelled) setErrorMessage("Failed to check subscription status")
                        console.error("Error checking subscription:", err)
                        return null
                    }),
                    getUserDetails().catch((err) => {
                        if (!cancelled) setErrorMessage("Failed to check enterprise status")
                        console.error("Error checking enterprise user:", err)
                        return null
                    }),
                ])
                if (cancelled) return
                if (subscriptionData?.isActive != null) setIsSubscribed(subscriptionData.isActive)
                if (subscriptionData?.currentPlan != null) setCurrentPlan(subscriptionData.currentPlan)

                let enterpriseActive = false
                if (userDetails?.user?.isMedRPTAdmin) {
                    enterpriseActive = true
                    setIsEnterpriseActive(true)
                } else if (userDetails?.user?.Enterprise) {
                    setHasEnterprise(true)
                    enterpriseActive = userDetails.user.Enterprise.isActive || false
                    setIsEnterpriseActive(enterpriseActive)
                }

                // Enterprise access always takes precedence: a stale/un-synced Advanced
                // subscription row should never hide Enterprise-tier features.
                if (subscriptionData?.isAdvanced != null) {
                    setIsAdvanced(enterpriseActive ? false : subscriptionData.isAdvanced)
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        checkAccess()
        return () => { cancelled = true }
    }, [user])

    return { user, isSubscribed, isAdvanced, currentPlan, isEnterpriseActive, hasEnterprise, loading, errorMessage, setErrorMessage }
}

export { useAccessCheck }
export default useAccessCheck
